import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

import { ErrorBoundary } from 'react-error-boundary';
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
} from 'react-resizable-panels';

import { ResizeHandleWithTooltip } from '@watershed/shared-frontend/components/ResizeHandleWithTooltip';

import { AIChatSidebarTrigger } from '../components/ai/components/AIChatSidebarTrigger';
import {
  useCoPilot,
  type CoPilotUIConfig,
} from '../components/ai-service/CoPilotContext';
import { ErrorFallback } from '../components/ErrorFallback';
import SidebarLayout from './SidebarLayout';

const DEFAULT_COPILOT_SIZE = 25;

export function CoPilotPanelLayout({
  activeCoPilot,
  isCoPilotOpen,
  toggleCoPilot,
  closeCoPilot,
  openCoPilot,
  children,
}: {
  activeCoPilot: CoPilotUIConfig;
  isCoPilotOpen: boolean;
  toggleCoPilot: () => void;
  closeCoPilot: () => void;
  openCoPilot: () => void;
  children: React.ReactNode;
}) {
  const copilotPanelRef = useRef<ImperativePanelHandle>(null);

  // Defer rendering the copilot component until the panel has been opened at
  // least once. This prevents MUI's TextareaAutosize (used by the chat input)
  // from computing its height while the panel has zero width, which would
  // cause the input to render as multi-line. Once mounted, the component stays
  // in the DOM so that state (e.g. chat messages) is preserved across toggles.
  const [hasBeenOpened, setHasBeenOpened] = useState(isCoPilotOpen);
  useEffect(() => {
    if (isCoPilotOpen) {
      setHasBeenOpened(true);
    }
  }, [isCoPilotOpen]);

  // Sync panel collapse/expand state with isCoPilotOpen
  useEffect(() => {
    if (isCoPilotOpen) {
      copilotPanelRef.current?.resize(DEFAULT_COPILOT_SIZE);
    } else {
      copilotPanelRef.current?.collapse();
    }
  }, [isCoPilotOpen]);

  return (
    <Box sx={{ position: 'relative' }}>
      <PanelGroup direction="horizontal" id="copilot-panels">
        <Panel
          id="main-content"
          order={1}
          defaultSize={isCoPilotOpen ? 100 - DEFAULT_COPILOT_SIZE : 100}
          minSize={50}
          style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
          {children}
        </Panel>

        {isCoPilotOpen && (
          <ResizeHandleWithTooltip
            id="copilot-resize-handle"
            direction="horizontal"
          />
        )}

        <Panel
          ref={copilotPanelRef}
          id="copilot-sidebar"
          order={2}
          defaultSize={isCoPilotOpen ? DEFAULT_COPILOT_SIZE : 0}
          minSize={10}
          maxSize={40}
          collapsible
          collapsedSize={0}
          onCollapse={closeCoPilot}
          onExpand={openCoPilot}
        >
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Box
              sx={{
                flexGrow: 1,
                overflow: 'hidden',
                height: '100dvh',
                borderLeft: (theme) => `0.5px solid ${theme.palette.grey30}`,
                backgroundColor: (theme) => theme.palette.grey05,
                // Establish a stacking context above whatever content sits
                // under this sidebar. The motivation for this is that the flexible
                // report document editor has floating buttons with a high z-index
                // which were displaying on top of the AI copilot content.
                position: 'relative',
                zIndex: 1,
              }}
            >
              {hasBeenOpened && (
                <activeCoPilot.component onClose={closeCoPilot} />
              )}
            </Box>
          </ErrorBoundary>
        </Panel>
      </PanelGroup>
      {!isCoPilotOpen && (
        <AIChatSidebarTrigger isOpen={isCoPilotOpen} onClick={toggleCoPilot} />
      )}
    </Box>
  );
}

export default function CoPilotLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const {
    activeCoPilot,
    isCoPilotOpen,
    toggleCoPilot,
    openCoPilot,
    closeCoPilot,
  } = useCoPilot();

  if (!activeCoPilot) {
    return <SidebarLayout>{children}</SidebarLayout>;
  }

  return (
    <SidebarLayout>
      <CoPilotPanelLayout
        activeCoPilot={activeCoPilot}
        isCoPilotOpen={isCoPilotOpen}
        toggleCoPilot={toggleCoPilot}
        closeCoPilot={closeCoPilot}
        openCoPilot={openCoPilot}
      >
        {children}
      </CoPilotPanelLayout>
    </SidebarLayout>
  );
}
